import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { assets$ } from '../streams/assets/assets$';
import { combineFolders } from '../components/EntityBrowser/utils';
import { Row } from '../components/EntityBrowser/types';
import { AssetRow } from '../components/AssetsBrowser/types';
import { reducedStream, reducer } from '@ardoq/rxbeach';
import { ArdoqError, setStateProperty } from '@ardoq/common-helpers';
import { type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import {
  setBookmarkName as setBookmarkNameAction,
  setBookmarkFolder as setBookmarkFolderAction,
  setSaveBookmarkError as setSaveBookmarkErrorAction,
} from './actions';
import { SelectedFolder } from '../components/FolderSelectDialog/types';

type SaveAsBookmarkStreamShape = {
  bookmarkName: string;
  selectedFolder: SelectedFolder;
  error: ArdoqError | null;
  isSaveBookmarkEnabled: boolean;
};

export type SaveAsBookmarkViewModelShape = SaveAsBookmarkStreamShape & {
  folderSelectDataSource: Row<AssetRow>[];
  i18nLocale: I18nLocale;
  locale: Locale;
};

const setBookmarkName = (
  state: SaveAsBookmarkStreamShape,
  bookmarkName: string
) => ({
  ...state,
  bookmarkName,
  isSaveBookmarkEnabled: bookmarkName.length > 0,
});

const saveAsBookmark$ = reducedStream<SaveAsBookmarkStreamShape>(
  'saveAsBookmark$',
  {
    bookmarkName: '',
    selectedFolder: null,
    error: null,
    isSaveBookmarkEnabled: false,
  },
  [
    reducer(setBookmarkNameAction, setBookmarkName),
    reducer(setBookmarkFolderAction, setStateProperty('selectedFolder')),
    reducer(setSaveBookmarkErrorAction, setStateProperty('error')),
  ]
);

const saveAsBookmarkViewModel$ = combineLatest([
  assets$,
  saveAsBookmark$,
  i18nLocale$,
  locale$,
]).pipe(
  map(
    ([
      { assetFoldersById },
      { bookmarkName, selectedFolder, error, isSaveBookmarkEnabled },
      i18nLocale,
      locale,
    ]): SaveAsBookmarkViewModelShape => {
      const dataSource = combineFolders(Object.values(assetFoldersById), []);
      return {
        folderSelectDataSource: dataSource,
        bookmarkName,
        error,
        selectedFolder,
        isSaveBookmarkEnabled,
        i18nLocale,
        locale,
      };
    }
  )
);

export { saveAsBookmarkViewModel$ };
